/**
 * Levenberg-Marquardt least-squares for the NN-Newton foil optimiser (see
 * foil-design-optimizer-DESIGN.md §8). Minimises ½·‖r(v)‖² over a design vector v by
 * the damped Gauss-Newton step (JᵀJ + λI)⁻¹ Jᵀr: λ is raised when a trial step is
 * rejected (more gradient-descent-like) and lowered when accepted (more Newton-like).
 *
 * Pure: no NN weights, no DOM, no state. The Jacobian defaults to central finite
 * differences (jacobianFD) so the module is exercised without trained NN weights, but
 * any J = ∂r/∂v provider can be injected. Optional box bounds are enforced by projecting
 * the candidate vector each step (mixed-inverse: clamp locked/limited DOFs).
 */

import { matT, matMul, matVec, addDiag, solveSPD } from './linalg';
import { jacobianFD } from './jacobianFD';

export interface LMOptions {
  /** J = ∂r/∂v provider; defaults to central finite differences. */
  jacobian?: (residual: (v: number[]) => number[], v: number[]) => number[][];
  /** Maximum LM iterations (default 100). */
  maxIter?: number;
  /** Initial damping λ (default 1e-3). */
  lambda0?: number;
  /** Box bounds; the candidate v is clamped into [min, max] each step. */
  bounds?: { min: number[]; max: number[] };
  /** Stop when the relative cost decrease and step size both fall below tol (default 1e-9). */
  tol?: number;
}

export interface LMResult {
  /** Best design vector found. */
  v: number[];
  /** Final cost ½·‖r(v)‖². */
  cost: number;
  /** Iterations performed. */
  iters: number;
  /** Cost after each accepted state, history[0] = initial cost. */
  history: number[];
}

/** ½·‖r‖². */
function cost(r: number[]): number {
  let s = 0;
  for (let i = 0; i < r.length; i++) s += r[i]! * r[i]!;
  return 0.5 * s;
}

/** Clamp v into [min, max] component-wise (no-op where bounds undefined). */
function project(v: number[], bounds?: { min: number[]; max: number[] }): number[] {
  if (!bounds) return v.slice();
  const out = new Array<number>(v.length);
  for (let j = 0; j < v.length; j++) {
    let x = v[j]!;
    const lo = bounds.min[j], hi = bounds.max[j];
    if (lo !== undefined && x < lo) x = lo;
    if (hi !== undefined && x > hi) x = hi;
    out[j] = x;
  }
  return out;
}

/**
 * Levenberg-Marquardt minimisation of ½‖residual(v)‖². Standard damped Gauss-Newton:
 * solve (JᵀJ + λI)·δ = −Jᵀr, accept if the cost drops (then λ ← λ/ν), else reject and
 * raise λ ← λ·ν. The candidate is projected into the bounds before evaluation. Stops on
 * tol (small relative cost drop and step) or maxIter. Pure — v0 is not mutated.
 */
export function lm(
  residual: (v: number[]) => number[],
  v0: number[],
  opts: LMOptions = {},
): LMResult {
  const jac = opts.jacobian ?? jacobianFD;
  const maxIter = opts.maxIter ?? 100;
  const tol = opts.tol ?? 1e-9;
  const bounds = opts.bounds;
  const nu = 10; // λ up/down factor on reject/accept.

  let v = project(v0, bounds);
  let r = residual(v);
  let c = cost(r);
  let lambda = opts.lambda0 ?? 1e-3;
  const history: number[] = [c];

  let iters = 0;
  for (; iters < maxIter; iters++) {
    if (c === 0) break;
    const J = jac(residual, v); // m×n
    const Jt = matT(J); // n×m
    const JtJ = matMul(Jt, J); // n×n
    const g = matVec(Jt, r); // n = Jᵀr

    // Inner loop: grow λ until a step reduces the cost (or we give up this iteration).
    let stepped = false;
    let innerTries = 0;
    while (innerTries < 30) {
      innerTries++;
      let delta: number[];
      try {
        // (JᵀJ + λI)·δ = −Jᵀr
        delta = solveSPD(addDiag(JtJ, lambda), g.map((x) => -x));
      } catch {
        lambda *= nu;
        continue;
      }
      const vNew = project(
        v.map((x, j) => x + delta[j]!),
        bounds,
      );
      const rNew = residual(vNew);
      const cNew = cost(rNew);

      if (cNew < c) {
        // Accept.
        let stepNorm = 0;
        for (let j = 0; j < v.length; j++) {
          const d = vNew[j]! - v[j]!;
          stepNorm += d * d;
        }
        stepNorm = Math.sqrt(stepNorm);
        const relDrop = (c - cNew) / (c + tol);

        v = vNew;
        r = rNew;
        c = cNew;
        history.push(c);
        lambda = Math.max(lambda / nu, 1e-12);
        stepped = true;

        if (relDrop < tol && stepNorm < tol) {
          iters++;
          return { v, cost: c, iters, history };
        }
        break;
      }
      // Reject: more damping.
      lambda *= nu;
    }

    if (!stepped) break; // Could not make progress; λ saturated.
  }

  return { v, cost: c, iters, history };
}
